import {
  AlertCircleIcon,
  Trash,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
} from 'lucide-react-native';
import { Controller, UseFormReturn, FieldArrayWithId } from 'react-hook-form';
import { findNodeHandle, UIManager } from 'react-native';
import { twJoin } from 'tailwind-merge';

import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionTrigger,
  AccordionTitleText,
  AccordionIcon,
  AccordionContent,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox, CheckboxIndicator, CheckboxIcon } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectInput,
  SelectIcon,
  SelectContent,
  SelectItem,
  SelectPortal,
  SelectBackdrop,
  SelectDragIndicatorWrapper,
  SelectDragIndicator,
  SelectScrollView,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { US_STATES } from '@/constants/states';
import { WorkHistoryFormValues } from '@/utils/profileHelpers';

interface WorkHistoryEntryProps {
  field: FieldArrayWithId;
  index: number;
  form: UseFormReturn<WorkHistoryFormValues>;
  onDeleteRequest: (index: number) => void;
  expandedItem: string[];
  setExpandedItem: (value: string[]) => void;
  scrollRef: React.RefObject<any>;
  roleDescRefs: React.MutableRefObject<any[]>;
  hasError?: boolean;
}

export const WorkHistoryEntry = ({
  field,
  index,
  form,
  onDeleteRequest,
  expandedItem,
  setExpandedItem,
  scrollRef,
  roleDescRefs,
  hasError = false,
}: WorkHistoryEntryProps) => {
  const workLocationError = form.formState.errors?.workHistory?.[index]?.workLocation;

  const cityError =
    workLocationError && typeof workLocationError === 'object' && 'city' in workLocationError
      ? (workLocationError as any).city?.message
      : undefined;

  const stateError =
    workLocationError && typeof workLocationError === 'object' && 'state' in workLocationError
      ? (workLocationError as any).state?.message
      : undefined;

  const entryHasError = !!form.formState.errors?.workHistory?.[index];

  const formatDateRange = (startYear?: string, endYear?: string, isPresent?: boolean) => {
    if (!startYear && !endYear && !isPresent) {
      return '';
    }

    return `${startYear || ''}${startYear && (endYear || isPresent) ? '–' : ''}${isPresent ? 'Present' : endYear || ''}`;
  };

  return (
    <Accordion
      key={field.id}
      type="single"
      collapsable
      className={twJoin('mb-2 w-full shadow-none', index === 0 && 'mt-4')}
      value={expandedItem}
      onValueChange={setExpandedItem}>
      <AccordionItem value={`item-${index}`}>
        <AccordionHeader className="m-0 w-full p-0">
          <AccordionTrigger className="m-0 w-full p-0">
            {({ isExpanded }: { isExpanded: boolean }) => (
              <HStack className="m-0 w-full items-start justify-between p-0">
                <HStack className="m-0 w-0 min-w-0 flex-1 items-start gap-2 p-0">
                  {isExpanded ? (
                    <AccordionIcon as={ChevronDownIcon} className="mt-1.5" />
                  ) : (
                    <AccordionIcon as={ChevronRightIcon} className="mt-1.5" />
                  )}
                  <VStack className="min-w-0 flex-1">
                    <AccordionTitleText className="max-w-full truncate text-base">
                      <Text className="font-medium text-base text-primary-700">
                        {form.watch(`workHistory.${index}.positionTitle`) ||
                          (form.watch(`workHistory.${index}.organization`)
                            ? form.watch(`workHistory.${index}.organization`)
                            : 'New Work History Entry')}
                      </Text>
                    </AccordionTitleText>
                    <Text className="text-sm text-primary-700">
                      {form.watch(`workHistory.${index}.organization`) || ''}
                    </Text>
                    <Text className="text-sm text-primary-700">
                      {formatDateRange(
                        form.watch(`workHistory.${index}.startYear`),
                        form.watch(`workHistory.${index}.endYear`),
                        form.watch(`workHistory.${index}.isPresent`)
                      )}
                    </Text>
                    {entryHasError && !isExpanded && (
                      <HStack className="mt-1 items-center">
                        <Icon as={AlertCircleIcon} size="xs" className="mr-1 text-error-600" />
                        <Text className="text-xs text-error-600">
                          Complete required fields for this entry
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </HStack>
                <Button
                  variant="link"
                  size="md"
                  onPress={() => onDeleteRequest(index)}
                  className="ml-1 flex-shrink-0 self-start p-2"
                  accessibilityLabel="Delete Work History Entry">
                  <Icon as={Trash} size="md" className="m-0 p-0 text-primary-600" />
                </Button>
              </HStack>
            )}
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>
          <VStack space="lg" className="pt-4">
            <Controller
              control={form.control}
              name={`workHistory.${index}.positionTitle`}
              render={({ field, fieldState }) => (
                <FormControl isInvalid={!!fieldState.error}>
                  <FormControlLabel>
                    <FormControlLabelText>Position</FormControlLabelText>
                  </FormControlLabel>
                  <Input>
                    <InputField
                      placeholder="Enter your position"
                      value={field.value}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                    />
                  </Input>
                  <FormControlError>
                    <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                    <FormControlErrorText size="xs">
                      {fieldState.error?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />
            <Controller
              control={form.control}
              name={`workHistory.${index}.organization`}
              rules={{
                required: {
                  value: true,
                  message: 'Organization is required',
                },
                validate: (value) => {
                  return !!value.trim() || 'Organization is required';
                },
              }}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isInvalid={!!fieldState.error} isRequired>
                    <FormControlLabel>
                      <FormControlLabelText>Organization</FormControlLabelText>
                    </FormControlLabel>
                    <Input>
                      <InputField
                        placeholder="Enter your organization"
                        value={field.value}
                        onChangeText={(text) => {
                          field.onChange(text);
                        }}
                        onBlur={field.onBlur}
                      />
                    </Input>
                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                      <FormControlErrorText size="xs">
                        {fieldState.error?.message || 'Organization is required'}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                );
              }}
            />
            <HStack space="md" className="w-full items-start">
              <Controller
                control={form.control}
                name={`workHistory.${index}.startYear`}
                render={({ field, fieldState }) => (
                  <FormControl isInvalid={!!fieldState.error} className="flex-1">
                    <FormControlLabel>
                      <FormControlLabelText>Start Year</FormControlLabelText>
                    </FormControlLabel>
                    <Input>
                      <InputField
                        placeholder="YYYY"
                        value={field.value}
                        onChangeText={(value) => {
                          field.onChange(value);
                        }}
                        onBlur={field.onBlur}
                      />
                    </Input>
                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                      <FormControlErrorText size="xs">
                        {fieldState.error?.message}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                )}
              />
              <VStack className="flex-1">
                <Controller
                  control={form.control}
                  name={`workHistory.${index}.endYear`}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isInvalid={!!fieldState.error}
                      className="flex-1"
                      isDisabled={form.watch(`workHistory.${index}.isPresent`)}>
                      <FormControlLabel>
                        <FormControlLabelText>End Year</FormControlLabelText>
                      </FormControlLabel>
                      <Input isDisabled={form.watch(`workHistory.${index}.isPresent`)}>
                        <InputField
                          placeholder="YYYY"
                          value={field.value}
                          onChangeText={field.onChange}
                          onBlur={field.onBlur}
                          editable={!form.watch(`workHistory.${index}.isPresent`)}
                        />
                      </Input>
                      <FormControlError>
                        <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                        <FormControlErrorText size="xs">
                          {fieldState.error?.message}
                        </FormControlErrorText>
                      </FormControlError>
                    </FormControl>
                  )}
                />
                <FormControl className="mt-1 flex flex-row items-center">
                  <Controller
                    control={form.control}
                    name={`workHistory.${index}.isPresent`}
                    render={({ field }) => (
                      <HStack className="items-center">
                        <Checkbox
                          value="present"
                          isChecked={field.value}
                          onChange={(checked) => {
                            field.onChange(checked);

                            if (checked) {
                              form.setValue(`workHistory.${index}.endYear`, '');
                            }
                          }}
                          size="md"
                          className="mr-2">
                          <CheckboxIndicator>
                            <CheckboxIcon as={CheckIcon} />
                          </CheckboxIndicator>
                        </Checkbox>
                        <FormControlLabelText>Present</FormControlLabelText>
                      </HStack>
                    )}
                  />
                </FormControl>
              </VStack>
            </HStack>
            <HStack space="md" className="w-full">
              <FormControl isInvalid={!!cityError} className="flex-1">
                <FormControlLabel>
                  <FormControlLabelText>City</FormControlLabelText>
                </FormControlLabel>
                <Controller
                  control={form.control}
                  name={`workHistory.${index}.workLocation.city`}
                  render={({ field }) => (
                    <Input>
                      <InputField
                        placeholder="City"
                        value={field.value}
                        onChangeText={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </Input>
                  )}
                />
                <FormControlError>
                  <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                  <FormControlErrorText size="xs">{cityError as string}</FormControlErrorText>
                </FormControlError>
              </FormControl>
              <FormControl isInvalid={!!stateError} className="flex-1">
                <FormControlLabel>
                  <FormControlLabelText>State</FormControlLabelText>
                </FormControlLabel>
                <Controller
                  control={form.control}
                  name={`workHistory.${index}.workLocation.state`}
                  render={({ field }) => (
                    <Select
                      selectedValue={field.value || undefined}
                      onValueChange={(value) => field.onChange(value)}>
                      <SelectTrigger className="h-9 justify-between">
                        <SelectInput placeholder="Select option" />
                        <SelectIcon className="mr-3" as={ChevronDownIcon} />
                      </SelectTrigger>
                      <SelectPortal>
                        <SelectBackdrop />
                        <SelectContent className="max-h-[70%]">
                          <SelectDragIndicatorWrapper>
                            <SelectDragIndicator />
                          </SelectDragIndicatorWrapper>
                          <SelectScrollView>
                            {US_STATES.map((state) => (
                              <SelectItem key={state.abbr} label={state.name} value={state.abbr} />
                            ))}
                          </SelectScrollView>
                        </SelectContent>
                      </SelectPortal>
                    </Select>
                  )}
                />
                <FormControlError>
                  <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                  <FormControlErrorText size="xs">{stateError as string}</FormControlErrorText>
                </FormControlError>
              </FormControl>
            </HStack>
            <Controller
              control={form.control}
              name={`workHistory.${index}.roleDescription`}
              render={({ field, fieldState }) => (
                <FormControl isInvalid={!!fieldState.error}>
                  <FormControlLabel>
                    <FormControlLabelText>Description</FormControlLabelText>
                  </FormControlLabel>
                  <Textarea size="sm">
                    <TextareaInput
                      ref={(el) => {
                        roleDescRefs.current[index] = el;
                      }}
                      placeholder="Enter your role description"
                      value={field.value}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                      numberOfLines={3}
                      multiline
                      textAlignVertical="top"
                      onFocus={() => {
                        const ref = roleDescRefs.current[index];

                        if (ref && scrollRef.current) {
                          setTimeout(() => {
                            const node = findNodeHandle(ref);

                            if (node) {
                              UIManager.measure(node, (x, y, width, height, pageX, pageY) => {
                                (scrollRef.current as any).scrollTo({
                                  y: pageY - 120,
                                  animated: true,
                                });

                                return undefined;
                              });
                            }
                          }, 100);
                        }
                      }}
                    />
                  </Textarea>
                  <FormControlError>
                    <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                    <FormControlErrorText size="xs">
                      {fieldState.error?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />
          </VStack>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
